import { useForm } from '@inertiajs/react';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toastFlash } from '@/lib/flash-toast';

export function CreateExternalPersonDialog({
  eventSlug,
}: {
  eventSlug: string;
}) {
  const [open, setOpen] = useState(false);
  const form = useForm({
    slug: '',
    fullName: '',
    category: '',
    role: '',
    githubUrl: '',
    linkedinUrl: '',
    redirectUrl: '',
    marketEnabled: false,
  });
  const { data, setData, processing, reset } = form;
  const errors = form.errors as unknown as Record<string, string | undefined>;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.post(`/admin/${eventSlug}/external-people`, {
      preserveScroll: true,
      onSuccess: (page) => {
        toastFlash(page, 'External person created successfully!');
        reset();
        setOpen(false);
      },
    });
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <Plus className="mr-2 h-4 w-4" />
        Add External Person
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add External Person</DialogTitle>
            <DialogDescription>
              Add a new external person (mentor, sponsor, judge, etc.)
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="create-external-person-slug">Slug</Label>
              <Input
                id="create-external-person-slug"
                placeholder="john-doe"
                disabled={processing}
                value={data.slug}
                onChange={(e) => setData('slug', e.target.value)}
              />
              {errors.slug && (
                <p className="text-destructive text-sm">{errors.slug}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="create-external-person-full-name">
                Full Name
              </Label>
              <Input
                id="create-external-person-full-name"
                placeholder="John Doe"
                disabled={processing}
                value={data.fullName}
                onChange={(e) => setData('fullName', e.target.value)}
              />
              {errors.fullName && (
                <p className="text-destructive text-sm">{errors.fullName}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="create-external-person-category">Category</Label>
              <Input
                id="create-external-person-category"
                placeholder="mentor, sponsor, judge..."
                disabled={processing}
                value={data.category}
                onChange={(e) => setData('category', e.target.value)}
              />
              {errors.category && (
                <p className="text-destructive text-sm">{errors.category}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="create-external-person-role">
                Role (Optional)
              </Label>
              <Input
                id="create-external-person-role"
                placeholder="CTO at Company"
                disabled={processing}
                value={data.role}
                onChange={(e) => setData('role', e.target.value)}
              />
              {errors.role && (
                <p className="text-destructive text-sm">{errors.role}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="create-external-person-github-url">
                GitHub URL (Optional)
              </Label>
              <Input
                id="create-external-person-github-url"
                placeholder="https://github.com/username"
                disabled={processing}
                value={data.githubUrl}
                onChange={(e) => setData('githubUrl', e.target.value)}
              />
              {errors.githubUrl && (
                <p className="text-destructive text-sm">{errors.githubUrl}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="create-external-person-linkedin-url">
                LinkedIn URL (Optional)
              </Label>
              <Input
                id="create-external-person-linkedin-url"
                placeholder="https://linkedin.com/in/username"
                disabled={processing}
                value={data.linkedinUrl}
                onChange={(e) => setData('linkedinUrl', e.target.value)}
              />
              {errors.linkedinUrl && (
                <p className="text-destructive text-sm">{errors.linkedinUrl}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="create-external-person-redirect-url">
                Redirect URL (Optional)
              </Label>
              <Input
                id="create-external-person-redirect-url"
                placeholder="https://example.com/profile"
                disabled={processing}
                value={data.redirectUrl}
                onChange={(e) => setData('redirectUrl', e.target.value)}
              />
              {errors.redirectUrl && (
                <p className="text-destructive text-sm">{errors.redirectUrl}</p>
              )}
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="flex flex-col gap-0.5">
                <Label htmlFor="create-external-person-market-enabled">
                  Banana Market access
                </Label>
                <p className="text-muted-foreground text-xs">
                  Let this person trade in the prediction market via their
                  GitHub login.
                </p>
              </div>
              <Switch
                id="create-external-person-market-enabled"
                checked={data.marketEnabled}
                onCheckedChange={(checked) => setData('marketEnabled', checked)}
                disabled={processing}
              />
            </div>

            {errors.base && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4">
                <p className="text-red-600 text-sm">{errors.base}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? 'Creating...' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
